import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../Api/baseQueryWithReauth';
import { TagTypes } from '../../../Api/Types/tagTypes';
import {
  buildApproveImportRequest,
  buildGetImportStatusRequest,
  buildRecomputeFa3ForExistingExpenseInvoicesRequest,
  getRecomputeFa3InvalidatesTags,
  getRetryApproveImportJobsInvalidatesTags,
  buildRetryApproveImportJobsRequest,
} from './invoiceImportApi.contract';
import {
  InvoiceImportResultDto,
  InvoiceImportStatusDto,
  InvoiceImportHistoryDto,
  InvoiceImportHistoryParams,
  PagedListDto,
  ImportInvoiceRequest,
  ApproveImportRequest,
  ApproveImportResultDto,
  RejectImportRequest,
  RecomputeFa3ForExistingExpenseInvoicesRequest,
  RetryApproveImportJobsRequest,
  Fa3SelectiveRecomputeResultDto,
  Fa3RetryApproveResultDto,
} from '../Types/invoiceImport.types';
import {
  normalizeImportHistoryResponse,
  normalizeImportStatusResponse,
} from './invoiceImportResponseMapper';

/**
 * RTK Query API slice for invoice import operations
 * Custom endpoints (not CRUD-based like genericApiFactory)
 */
export const invoiceImportApi = createApi({
  reducerPath: 'invoiceImportApi',
  baseQuery: baseQueryWithReauth, // Use enhanced baseQuery with automatic refresh
  tagTypes: [
    TagTypes.InvoiceImport,
    TagTypes.InvoiceImportHistory,
    TagTypes.Invoice,
    TagTypes.ExpenseInvoice,
  ],
  endpoints: (builder) => ({
    /**
     * POST /api/admin/invoices/import
     * Upload file and initiate import job
     */
    importInvoice: builder.mutation<InvoiceImportResultDto, ImportInvoiceRequest>({
      query: ({ formData, sourceCountryIso }) => ({
        url: '/invoices/import',
        method: 'POST',
        body: formData,
        params: sourceCountryIso ? { sourceCountryIso } : undefined,
        // Don't set Content-Type header - browser will set multipart/form-data with boundary
      }),
      invalidatesTags: [
        TagTypes.InvoiceImportHistory,
        { type: TagTypes.Invoice, id: TagTypes.List },
      ],
    }),

    /**
     * GET /api/admin/invoices/import/{id}/status
     * Poll import job status
     */
    getImportStatus: builder.query<InvoiceImportStatusDto, number>({
      query: buildGetImportStatusRequest,
      transformResponse: (response: InvoiceImportStatusDto) =>
        normalizeImportStatusResponse(response),
      providesTags: (result, error, importJobId) => [
        { type: TagTypes.InvoiceImport, id: importJobId },
      ],
    }),

    /**
     * GET /api/admin/invoices/import/history
     * Fetch import history with pagination and filters
     */
    getImportHistory: builder.query<
      PagedListDto<InvoiceImportHistoryDto>,
      InvoiceImportHistoryParams
    >({
      query: ({
        pageNumber = 1,
        pageSize = 20,
        status,
        searchTerm,
        importerType,
        fromDate,
        toDate,
      }) => {
        const params: Record<string, any> = { pageNumber, pageSize };
        if (status) params.status = status;
        if (searchTerm) params.searchTerm = searchTerm;
        if (importerType) params.importerType = importerType;
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;

        return {
          url: '/invoices/import/history',
          params,
        };
      },
      transformResponse: (response: PagedListDto<InvoiceImportHistoryDto>) =>
        normalizeImportHistoryResponse(response),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((item) => ({
                type: TagTypes.InvoiceImport as const,
                id: item.importJobId,
              })),
              TagTypes.InvoiceImportHistory,
            ]
          : [TagTypes.InvoiceImportHistory],
    }),

    /**
     * POST /api/admin/invoices/import/{id}/approve
     * Phase 4: Approve import with optional user corrections
     */
    approveImport: builder.mutation<ApproveImportResultDto, ApproveImportRequest>({
      query: buildApproveImportRequest,
      // RTK Query calls invalidatesTags on every settled outcome, not only on success — a
      // rejected approve must never invalidate the import history or invoice caches (S5c
      // Binding scope).
      invalidatesTags: (result, error, { importJobId }) => {
        if (error) {
          return [];
        }

        const tags: Array<
          | TagTypes.InvoiceImportHistory
          | { type: TagTypes.InvoiceImport | TagTypes.Invoice; id: number | string }
        > = [
          { type: TagTypes.InvoiceImport, id: importJobId },
          TagTypes.InvoiceImportHistory,
          { type: TagTypes.Invoice, id: TagTypes.List },
        ];
        if (result?.invoiceId) {
          tags.push({ type: TagTypes.Invoice, id: result.invoiceId });
        }
        return tags;
      },
    }),

    /**
     * POST /api/admin/invoices/import/{id}/reject
     * Phase 4: Reject import with reason
     */
    rejectImport: builder.mutation<void, RejectImportRequest>({
      query: ({ importJobId, reason }) => ({
        url: `/invoices/import/${importJobId}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { importJobId }) => [
        { type: TagTypes.InvoiceImport, id: importJobId },
        TagTypes.InvoiceImportHistory,
      ],
    }),

    /**
     * POST /api/admin/invoices/import/{id}/retry
     * Phase 4: Retry failed import (max 3 attempts)
     */
    retryImport: builder.mutation<InvoiceImportResultDto, number>({
      query: (importJobId) => ({
        url: `/invoices/import/${importJobId}/retry`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, importJobId) => [
        { type: TagTypes.InvoiceImport, id: importJobId },
        TagTypes.InvoiceImportHistory,
        { type: TagTypes.Invoice, id: TagTypes.List },
      ],
    }),

    /**
     * POST /api/admin/invoices/import/{id}/abandon
     * S1/S2: manually abandon a RequiresReview/Rejected import (no body, 204 No Content).
     * A rejected write must never simulate success by invalidating the cache — same pattern
     * as approveImport above.
     */
    abandonImport: builder.mutation<void, number>({
      query: (importJobId) => ({
        url: `/invoices/import/${importJobId}/abandon`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, importJobId) => {
        if (error) {
          return [];
        }

        return [
          { type: TagTypes.InvoiceImport, id: importJobId },
          TagTypes.InvoiceImportHistory,
        ];
      },
    }),

    /**
     * POST /api/admin/invoices/import/fa3/recompute
     * Selective FA(3) recompute for existing expense invoices (dry-run / execute)
     */
    recomputeFa3ForExistingExpenseInvoices: builder.mutation<
      Fa3SelectiveRecomputeResultDto,
      RecomputeFa3ForExistingExpenseInvoicesRequest
    >({
      query: buildRecomputeFa3ForExistingExpenseInvoicesRequest,
      invalidatesTags: getRecomputeFa3InvalidatesTags(),
    }),

    /**
     * POST /api/admin/invoices/import/fa3/retry-approve
     * Split-flow retry/approve operation for import jobs (dry-run / execute)
     */
    retryApproveImportJobs: builder.mutation<
      Fa3RetryApproveResultDto,
      RetryApproveImportJobsRequest
    >({
      query: buildRetryApproveImportJobsRequest,
      invalidatesTags: (result, error, { importJobIds }) =>
        getRetryApproveImportJobsInvalidatesTags(importJobIds),
    }),

    /**
     * GET /api/admin/invoices/import/{id}/download
     * Phase 4: Download original uploaded file
     */
    downloadImportFile: builder.mutation<Blob, number>({
      query: (importJobId) => ({
        url: `/invoices/import/${importJobId}/download`,
        headers: {
          accept: '*/*',
        },
        responseHandler: async (response) => {
          if (response.ok) {
            return await response.blob();
          }

          const contentType = response.headers.get('content-type') ?? '';
          const rawBody = await response.text();

          if (!rawBody) {
            return null;
          }

          const looksLikeJson =
            contentType.includes('application/json') ||
            contentType.includes('application/problem+json') ||
            rawBody.trim().startsWith('{') ||
            rawBody.trim().startsWith('[');

          if (!looksLikeJson) {
            return rawBody;
          }

          try {
            return JSON.parse(rawBody);
          } catch {
            return rawBody;
          }
        },
      }),
      invalidatesTags: (result, error, importJobId) => [
        { type: TagTypes.InvoiceImport, id: importJobId },
      ],
    }),
  }),
});

export const {
  useImportInvoiceMutation,
  useGetImportStatusQuery,
  useLazyGetImportStatusQuery,
  useGetImportHistoryQuery,
  useApproveImportMutation,
  useRejectImportMutation,
  useRetryImportMutation,
  useAbandonImportMutation,
  useRecomputeFa3ForExistingExpenseInvoicesMutation,
  useRetryApproveImportJobsMutation,
  useDownloadImportFileMutation,
} = invoiceImportApi;
